/*******************************************************************************
  SiteStatus.js

  Author: Mark Duckworth;

*******************************************************************************/

import React, { Component } from "react";
import { PollSiteStatus } from "../apihelpers/poller";
import {
  validateRMSStatus,
  validateModeStatus,
  validateHealthStatus,
  contactSupportText,
} from "./helper/homePage/siteStatusValidation";

const POLL_PERIOD = 20000;

/**
 * SiteStatus class shows basic status of a single Facilities site
 * The siteid and sitename must be given in props.
 * This class polls the task manager API to get site status, and it
 * displays the status in a simple table.
 */
class SiteStatus extends Component {
  constructor(props) {
    super(props);
    this.state = {
      siteStatus: {},
    };
    this.statusPoller = new PollSiteStatus(this);
  }

  async componentDidMount() {
    this.statusPoller.startPoll(0, POLL_PERIOD, this.props.siteid);
  }

  componentWillUnmount() {
    this.statusPoller.stopPoll();
  }

  //***************************************************************************
  // Callback from the status poller.
  // siteResponse - object response from the GET /site/<id> API
  //***************************************************************************
  siteStatusCb(siteResponse) {
    this.setState({ siteStatus: siteResponse });
  }

  /**
   * getDocked() - safely get the docked status for the robot,
   * return "Yes", "No", or "" if there is no status.
   */
  getDocked() {
    let docked = "";
    if (
      this.state.siteStatus &&
      this.state.siteStatus.robotinfo &&
      this.state.siteStatus.robotinfo.health &&
      this.state.siteStatus.robotinfo.health.PowerStatus &&
      this.state.siteStatus.robotinfo.health.PowerStatus.DockSenseBit
    ) {
      if (this.state.siteStatus.robotinfo.health.PowerStatus.DockSenseBit === "1") {
        docked = "Yes";
      } else {
        docked = "No";
      }
    }
    return docked;
  }

  /**
   * getStopButton() - safely get the EStop button status for the robot,
   * return [status, isValid, detail] where status is "Pressed", "Normal", or ""
   */
  getStopButton() {
    if (
      this.state.siteStatus &&
      this.state.siteStatus.robotinfo &&
      this.state.siteStatus.robotinfo.health &&
      this.state.siteStatus.robotinfo.health.PowerStatus &&
      this.state.siteStatus.robotinfo.health.PowerStatus.Estop
    ) {
      if (this.state.siteStatus.robotinfo.health.PowerStatus.Estop === "1") {
        return [
          "Pressed",
          false,
          `Ava's stop button has been pressed${contactSupportText(true)}`,
        ];
      } else {
        return ["Normal", true, ""];
      }
    }
    return ["", false, ""];
  }

  /**
   * getSiteMode() - get the current mode, considering facilities state and maintenance mode
   * report maintenance mode only if facilities status is IDLE and robot is in maintenance
   * return [mode, isValid, detail] where mode is "IDLE", "INSPECTION", "DISINFECTION", "SECURITY_PATROL", "RECEPTIONIST", "MAINTENANCE", or ""
   */
  getSiteMode() {
    if (!this.state.siteStatus) return ["", false, ""];

    let mode = this.state.siteStatus.facilitiesState;
    if (mode === "IDLE" && this.state.siteStatus.robotinfo?.configuration?.enabled === false) {
      mode = "MAINTENANCE";
    }
    const [isValid, detail] = validateModeStatus(mode);
    return [mode, isValid, detail];
  }

  /**
   * getRobotLink() - get the robot ID and URL link to RMS
   */
  getRobotLink() {
    let robotLink = "Disconnected";
    let connected = false;
    if (
      this.state.siteStatus &&
      this.state.siteStatus.robotinfo &&
      this.state.siteStatus.robotinfo.status &&
      this.state.siteStatus.robotinfo.status.connected
    ) {
      connected = true;
    }
    if (this.state.siteStatus && this.state.siteStatus.robotid) {
      const href = `https://${this.props.rmsname}/cust_admin/application/robots/detail.html?robot=${this.state.siteStatus.robotid}`;
      robotLink = (
        <>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={!connected ? "invalidStatus" : ""}
          >
            {this.state.siteStatus.robotid}
          </a>
          {!connected ? " disconnected" : ""}
        </>
      );
      return [
        robotLink,
        connected,
        !connected ? `Ava disconnected from domain${contactSupportText(true)}` : ``,
      ];
    }
    return [robotLink, false, `No Ava found${contactSupportText()}`];
  }
  /**
   * getIoTStatus() - safely get the IoT connection status for the robot,
   * return [status, isValid, detail] where status is "OK", "Disconnected", "Not registered", or ""
   */
  getIoTStatus() {
    let iotStatus = "";
    let detail = "";
    if (this.state.siteStatus && this.state.siteStatus.telemetry) {
      const { thingExists, connected } = this.state.siteStatus.telemetry;

      // check connection status if exists in AWS IoT
      if (thingExists === true) {
        iotStatus = connected ? "OK" : "Disconnected";
        detail = connected
          ? ""
          : `Ava disconnected from Automation server${contactSupportText()}`;
      } else {
        iotStatus = "Not registered";
        detail = `Ava not registered to Automation server${contactSupportText()}`;
      }
    }
    return [iotStatus, iotStatus === "OK", detail];
  }

  /**
   * getRMS() - safely get the RMS ID for the site,
   * return [rmsId, isValid, detail] where rmsId is the RMS name without domain
   */
  getRMS() {
    if (this.props.rmsname) {
      const [isValid, detail] = validateRMSStatus(this.state.siteStatus);
      return [this.props.rmsname.split(".")[0], isValid, detail];
    }
    return ["", false, ""];
  }

  /**
   * getRobotHealth() - get the robot health status
   * return [health, isValid, detail] where health is the overallHealth string
   */
  getRobotHealth() {
    const robotInfo = this.state.siteStatus?.robotinfo;
    const isLocalized = robotInfo?.status?.inLocalization;
    let health = robotInfo?.health?.overallHealth;
    const healthDetails = robotInfo?.health?.health;

    if ((health === "OK" || health === "OK_WARN") && !isLocalized) health = "UNLOCALIZED";
    const [isValid, detail] = validateHealthStatus(health, healthDetails);
    return [health, isValid, detail];
  }
  /**
   * getRobotBatteryCharge() - safely get the battery charge percentage for the robot,
   * return [batteryCharge, isValid, detail] where batteryCharge is "XX%"
   */
  getRobotBatteryCharge() {
    const batteryCharge =
      this.state.siteStatus &&
      this.state.siteStatus.robotinfo &&
      this.state.siteStatus.robotinfo.health &&
      Math.round(parseFloat(this.state.siteStatus.robotinfo.health.batteryCharge)).toString() +
        "%";
    return [batteryCharge, true, ""];
  }

  /**
   * getStatus() - renders a status value with appropriate styling based on validity
   * status - [value, isValid, detail]
   * return JSX span element with appropriate styling based on validity
   */
  getStatus(status) {
    const [value, valid, detail] = status;
    if (valid) {
      return <span>{value}</span>;
    } else {
      // Show detail only for sysadmin
      return (
        <span className="invalidStatus" title={detail}>
          {value}
        </span>
      );
    }
  }
  render() {
    // Calculate all statuses together to determine if any are invalid
    // to show the invalid status icon for the site
    const statuses = {
      stopButton: this.getStopButton(),
      siteMode: this.getSiteMode(),
      robotLink: this.getRobotLink(),
      iotStatus: this.getIoTStatus(),
      rms: this.getRMS(),
      robotHealth: this.getRobotHealth(),
      batteryCharge: this.getRobotBatteryCharge(),
    };

    // Check if there is any invalid status
    const hasInvalidStatus = Object.values(statuses).some(([, valid]) => !valid);

    return (
      <div id="siteStatusTable" style={{ paddingLeft: "15px " }}>
        <h4 style={{ textAlign: "left", marginBottom: "0" }}>
          {this.props.sitename}
          {hasInvalidStatus && <span className="siteInvalidStatus">⦸</span>}

          {this.props.sysadmin ? (
            <>
              <br />{" "}
              <span className="status-label" style={{ fontSize: "initial" }}>
                (Site ID: {this.props.siteid})
              </span>{" "}
            </>
          ) : null}
        </h4>
        <table>
          <tbody>
            <tr>
              <td style={{ padding: 0 }}>
                <ul className="list-status">
                  <li>
                    <span className="status-label">Ava ID: </span>
                    {this.getStatus(statuses.robotLink)}
                  </li>
                  <li>
                    <span className="status-label">Docked: </span>
                    <span>{this.getDocked()}</span>
                  </li>
                  <li>
                    <span className="status-label">Ava Health: </span>
                    {this.getStatus(statuses.robotHealth)}
                  </li>
                </ul>
              </td>
              <td>
                <ul className="list-status">
                  <li>
                    <span className="status-label">Domain: </span>
                    {this.getStatus(statuses.rms)}
                  </li>
                  <li>
                    <span className="status-label">Battery Charge: </span>
                    {this.getStatus(statuses.batteryCharge)}
                  </li>
                  <li>
                    <span className="status-label">Automation Server: </span>
                    {this.getStatus(statuses.iotStatus)}
                  </li>
                </ul>
              </td>
              <td style={{ alignContent: "flex-start" }}>
                <ul className="list-status">
                  <li>
                    <span className="status-label">Mode: </span>
                    {this.getStatus(statuses.siteMode)}
                  </li>
                  <li>
                    <span className="status-label">Stop Button: </span>
                    {this.getStatus(statuses.stopButton)}
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
}
export default SiteStatus;
